import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, ApiError } from '../client';
import { queryKeys } from './keys';
import type {
  AuditEntryDetail,
  AuditFacets,
  AuditFilters,
  AuditPage,
  AuditSummary,
  AuditTracing,
  AuditTracingStart,
} from '../types';

function auditFilterParams(filters: AuditFilters): URLSearchParams {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    for (const one of Array.isArray(value) ? value : [value]) {
      if (one) params.append(key, String(one));
    }
  }
  return params;
}

function auditQueryString(filters: AuditFilters, cursor: string | null, limit: number): string {
  const params = auditFilterParams(filters);
  params.set('limit', String(limit));
  if (cursor) params.set('cursor', cursor);
  return params.toString();
}

/**
 * One keyset page: `cursor` is the previous page's `next_cursor` (null for
 * the first). The page on screen stays while the next one loads.
 */
export function useAuditLog(filters: AuditFilters, cursor: string | null, limit: number) {
  return useQuery({
    queryKey: queryKeys.audit(filters, cursor, limit),
    queryFn: () => api<AuditPage>(`/api/audit-log?${auditQueryString(filters, cursor, limit)}`),
    placeholderData: (previous) => previous,
  });
}

/** The counts over the strip on the page; `order` plays no part, so it is dropped. */
export function useAuditSummary(filters: AuditFilters) {
  const { order: _order, ...matching } = filters;
  return useQuery({
    queryKey: queryKeys.auditSummary(matching),
    queryFn: () => api<AuditSummary>(`/api/audit-log/summary?${auditFilterParams(matching).toString()}`),
    placeholderData: (previous) => previous,
  });
}

export function useAuditEntry(id: string) {
  return useQuery({
    queryKey: queryKeys.auditEntry(id),
    queryFn: () => api<AuditEntryDetail>(`/api/audit-log/${id}`),
    staleTime: Infinity, // entries never change
  });
}

export function useAuditFacets() {
  return useQuery({ queryKey: queryKeys.auditFacets, queryFn: () => api<AuditFacets>('/api/audit-log/facets') });
}

/** How often the Audit Log asks whether read tracing is on; the countdown runs locally between asks. */
export const TRACING_POLL_MS = 60_000;

const TRACING_PATH = '/api/audit-log/tracing';

/** Whether read tracing is on, for every audit reader. */
export function useAuditTracing() {
  return useQuery({
    queryKey: queryKeys.auditTracing,
    queryFn: () => api<AuditTracing>(TRACING_PATH),
    refetchInterval: TRACING_POLL_MS,
  });
}

/**
 * Keeps the answer as the tracing state, then reloads the audit queries: the list and the summary now hold the
 * start or stop entry (and the traced reads follow the new state).
 */
function useTracingWrite<TInput>(write: (input: TInput) => Promise<AuditTracing>, success: string, failureShown: boolean) {
  const client = useQueryClient();
  return useMutation({
    meta: { success, failureShown },
    mutationFn: write,
    onSuccess: (tracing) => {
      client.setQueryData(queryKeys.auditTracing, tracing);
      return client.invalidateQueries({ queryKey: ['audit'] });
    },
    // Already on (someone else started it): show it.
    onError: (error) => {
      if (error instanceof ApiError && error.status === 409) void client.invalidateQueries({ queryKey: queryKeys.auditTracing });
    },
  });
}

/** Turns read tracing on (`audit.tracing.manage`); the dialog explains a refusal itself. */
export function useStartTracing() {
  return useTracingWrite((body: AuditTracingStart) => api<AuditTracing>(TRACING_PATH, 'PUT', body), 'Read tracing started', true);
}

/** Turns read tracing off now (`audit.tracing.manage`); off already is not an error. */
export function useStopTracing() {
  return useTracingWrite<void>(() => api<AuditTracing>(TRACING_PATH, 'DELETE'), 'Read tracing stopped', false);
}
